import type { CloudConfig, Manual, RemoteRef } from "../types";
import type { CloudAdapter } from "./types";

declare global {
  interface Window {
    CloudKit?: any;
  }
}

const CLOUDKIT_SRC = "https://cdn.apple-cloudkit.com/ck/2/CloudKit.js";

function loadScript(src: string, id: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function getDatabase(config: CloudConfig["apple"]) {
  if (!config.container) throw new Error("APPLE_CLOUDKIT_CONTAINER가 필요합니다.");
  await loadScript(CLOUDKIT_SRC, "apple-cloudkit-js");
  window.CloudKit.configure({
    containers: [
      {
        containerIdentifier: config.container,
        apiTokenAuth: {
          apiToken: config.apiToken
        },
        environment: "production"
      }
    ]
  });
  const container = window.CloudKit.getDefaultContainer();
  await container.setUpAuth();
  const user = await container.whenUserSignsIn();
  if (!user) throw new Error("iCloud 로그인이 필요합니다.");
  return container.privateCloudDatabase;
}

export function createAppleCloudKitAdapter(config: CloudConfig["apple"]): CloudAdapter {
  return {
    provider: "apple",
    mode: "live",
    async connect() {
      await getDatabase(config);
      sessionStorage.setItem("allmanual.apple.connected", "live");
    },
    async disconnect() {
      sessionStorage.removeItem("allmanual.apple.connected");
    },
    async uploadManual(manual: Manual): Promise<RemoteRef> {
      const database = await getDatabase(config);
      const recordName = `manual_${manual.id.replaceAll("-", "_")}`;
      await database.saveRecords({
        records: [
          {
            recordType: "ALLManualManual",
            recordName,
            fields: {
              title: { value: manual.title },
              brand: { value: manual.brand || "" },
              model: { value: manual.model || "" },
              payload: { value: JSON.stringify(manual) },
              updatedAt: { value: new Date().toISOString() }
            }
          }
        ]
      });
      return {
        provider: "apple",
        remoteId: recordName,
        syncedAt: new Date().toISOString(),
        mode: "live"
      };
    },
    async listManuals() {
      const database = await getDatabase(config);
      const result = await database.performQuery({
        recordType: "ALLManualManual",
        sortBy: [{ fieldName: "updatedAt", ascending: false }]
      });
      return (result.records || [])
        .map((record: any) => record.fields?.payload?.value)
        .filter(Boolean)
        .map((payload: string) => JSON.parse(payload));
    }
  };
}
